import {Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import { BaseComponent, IWMSStandaloneEditorConstructionOptions, WMSEditorComponent, WmsMessageComponent } from '@wms/app/components';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { WmsToggleComponent } from "@wms/components";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {TabsModule} from "ngx-bootstrap/tabs";
import { OrderByPipe, FilterPipe } from '@wm/components-base';
import { ApisService } from '@wms/app/services';
import moment from "moment/moment";
import { DB_CONSTANTS } from '@wms/app/core/utils/utils';
import {WebscoketService} from "@wms/app/webscoket.service";

@Component({
  selector: 'wms-config',
  standalone: true,
  imports: [CommonModule, TooltipModule, WmsToggleComponent, FormsModule, TabsModule, WMSEditorComponent, OrderByPipe, FilterPipe,WmsMessageComponent],
  templateUrl: './config.component.html',
  styleUrls: ['./config.component.less']
})
export class ConfigComponent extends BaseComponent implements OnChanges{

  @Input() serviceId: string;
  @Input() inputParams: any;
  @Output() addParam: EventEmitter<any> = new EventEmitter
  @Output() removeParam: EventEmitter<any> = new EventEmitter
  @Output() urlChange: EventEmitter<any> = new EventEmitter();
  @Output() testRun: EventEmitter<any> = new EventEmitter();

  @ViewChild("codeEditor") wmsEditorEl: WMSEditorComponent;

  editorOptions: IWMSStandaloneEditorConstructionOptions = {
    value: "",
  };
  testDisabled: boolean = false;
  paramGroup:any = {
    'query' : 'query',
    'path': 'path',
    'body': 'body'
  };
  webServiceTypes     = ['RestService', 'SoapService', 'FeedService', 'WebsocketService'];
  webServiceTabs = [
    {name: 'Authorization', id: 'authorization'},
    {name: 'Headers', id: 'headers'},
    {name: 'Body', id: 'body'},
    {name: 'Query Params', id: 'query'},
    {name: 'Path Params', id: 'path'}
  ];
  activeWebServiceType = 'WEBSOCKET';
  externalLinks:any ={
    IMPORTING_REST_SERVICE : "/app-development/services/web-services/rest-services/"
  };
  pathInforMessage:any = {
    caption : this.locale.MESSAGE_NO_PATH_PARAM_FOUND+ '<a href='+this.externalLinks.IMPORTING_REST_SERVICE+' target="_blank">('+this.locale.LABEL_MORE_INFO+')</a>',
    type : 'info',
    show: true
  }
  webSocketService:WebscoketService;


  constructor( public apiService: ApisService){
    super();
    this.webSocketService = inject(WebscoketService);
  }

  ngOnChanges(){
    if(this.webSocketService.ws.editMode){
      this.webSocketService.ws.inputParams?.forEach((param: { [x: string]: any; format: any; type: any; }) => {
        param['format'] = param.format || param.type || 'string'
      });
      setTimeout(() => {
        this.wmsEditorEl?.setEditorValue(this.webSocketService.ws.responseBody || "")
      });
    }
  }

  get filteredPathParams() {
    return this.webSocketService.ws.inputParams?.filter((param: { in: any; }) => param.in === (this.webSocketService.ws.paramType.PATH).toLowerCase());
  }

  showResult(value:any){
    this.wmsEditorEl?.setEditorValue(value || "")
  }

  isTesting(event:any){
    this.webSocketService.ws.testNotRequired = event;
    this.webSocketService.ws.serviceTested = true;
  }

  onParamTypeChange(paramObj:any){
    let testValue;
    if (paramObj.format) {
        paramObj.type = paramObj.format;
        // update items.type for header params
        if (paramObj.items) {
            paramObj.items.type = paramObj.type;
        }
    } else {
        paramObj.format = paramObj.type;
    }
    // set serviceTested flag to true inorder to enable save button when there is any change in param type
    this.webSocketService.ws.serviceTested = true;
    // update this.webSocketService.ws.cachedHeaderParamsType on param type change
    if (paramObj.in === this.webSocketService.ws.paramType.HEADER.toLowerCase()) {
        this.webSocketService.ws.cachedHeaderParamsType[paramObj.name] = paramObj.type;
    }

    // update the test value of param if it's type is changed a to server side property like Current Date, Time, Timestamp and DateTime
    if (['DATE', 'TIME', 'DATETIME', 'TIMESTAMP'].indexOf(paramObj.format) != -1) {
        testValue = this.getCurrentValuesForServerSideProps(paramObj);
        if (paramObj.in === this.webSocketService.ws.paramType.HEADER.toLowerCase()) {
            this.webSocketService.ws.restInfoHeaders[paramObj.name] = testValue;
        } else if (paramObj.in === this.webSocketService.ws.paramType.QUERY.toLowerCase()) {
            this.webSocketService.ws.restInfoQuery[paramObj.name] = testValue;
        }
        this.editParamterValueInURL(paramObj, this.webSocketService.ws.restInfoQuery[paramObj.name]);
    }
  }

  editParamterValueInURL(param:any, value:any){
      // set the serviceTested flag to true inorder to enable save when there is any change in param value
      this.webSocketService.ws.serviceTested = true;
      if (!this.webSocketService.ws.activeServiceType || !this.webSocketService.ws.editMode) {
          this.webSocketService.ws.restURL = this.editParameterInURL(this.webSocketService.ws.restURL, param.name, value);
          return;
      }
      this.webSocketService.ws.webSocketUrl = this.editParameterInURL(this.webSocketService.ws.webSocketUrl, param.name, value);
  }

  editParameterInURL(sourceURL: string, paramName: any, paramValue: string) {
    let url = sourceURL.split("?")[0],
        param,
        params_arr = [],
        queryString = (sourceURL.indexOf("?") !== -1) ? sourceURL.split("?")[1] : "";
    if (queryString !== "") {
        params_arr = queryString.split("&");
        for (let i = params_arr.length - 1; i >= 0; i -= 1) {
            param = params_arr[i].split("=")[0];
            if (param === paramName) {
                params_arr[i] = param  + '=' + paramValue;
                break;
            }
        }
        url = url + "?" + params_arr.join("&");
    }
    return url;
  }

  removeParamFn(param:any){
    this.removeParam.emit(param);
  }

  onNewParamTypeChange(paramObj:any){
    paramObj.type = paramObj.format;
    if (paramObj.in === this.webSocketService.ws.paramType.QUERY.toLowerCase()) {
        this.webSocketService.ws.isInvalidQueryParamType = false;
    }

    // update the test value of param if it's type is changed a to server side property like Current Date, Time, Timestamp and DateTime
    if (['DATE', 'TIME', 'DATETIME', 'TIMESTAMP'].indexOf(paramObj.format) != -1) {
        paramObj.value = this.getCurrentValuesForServerSideProps(paramObj);
    }
  }

  getCurrentValuesForServerSideProps(paramObj:any) {
    let testValue;
    // auto populating the Test Values for Server Side Properties like Current Date, Time , DateTime and Timestamp
    switch (paramObj.format) {
        case DB_CONSTANTS.SERVER_SIDE_PROPERTIES.CURRENT_DATE.value:
            testValue = moment().format("YYYY-MM-DD");
            break;
        case DB_CONSTANTS.SERVER_SIDE_PROPERTIES.CURRENT_TIME.value:
            testValue = moment().format('HH:mm:ss');
            break;
        case DB_CONSTANTS.SERVER_SIDE_PROPERTIES.CURRENT_DATE_TIME.value:
            testValue = moment().format('YYYY-MM-DDTHH:mm:ss');
            break;
        case DB_CONSTANTS.SERVER_SIDE_PROPERTIES.CURRENT_TIMESTAMP.value:
            testValue = moment().unix().toString();
            break;
    }
    return testValue;
}

  addParamFn(type:any){
    this.addParam.emit(type);
  }

  onUrlChange(webSocketUrl:any){
    this.urlChange.emit(webSocketUrl);
  }

  test(){
    this.testRun.emit()
  }

  extractType(type: string ){
    return type
  }
}
